import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useUserContext } from "../contexts/UserContext";
import { isFreePlanUser, isLightPlanUser } from "../utils/plan";
import { openUpgradeForm } from "../components/FreePlanUpgradeDialog";
import { apiGetJson, apiPutJson } from "../utils/apiClient";

type MethodRow = {
  name: string;
  carrier: string;
  status: "ready" | "missing";
  cost: number | null;
  type: "paid" | "none";
  note: string;
};

const INITIAL_ROWS: MethodRow[] = [
  { name: "宅配便", carrier: "ヤマト運輸", status: "ready", cost: 720, type: "paid", note: "標準宅配便" },
  { name: "クール便", carrier: "ヤマト運輸", status: "ready", cost: 920, type: "paid", note: "冷蔵対応" },
  { name: "クール冷凍便", carrier: "ヤマト運輸", status: "ready", cost: 1080, type: "paid", note: "冷凍対応" },
  { name: "大型宅配便", carrier: "その他", status: "ready", cost: 1500, type: "paid", note: "大型・重量物向け" },
  { name: "追跡可能メール便", carrier: "ヤマト運輸", status: "ready", cost: 220, type: "paid", note: "小型・軽量向け" },
  { name: "メール便", carrier: "未設定", status: "missing", cost: null, type: "paid", note: "コスト未設定" },
  { name: "コンビニ受取", carrier: "-", status: "missing", cost: 0, type: "none", note: "自動で0円（編集不可）" },
  { name: "発送を伴わない", carrier: "-", status: "missing", cost: 0, type: "none", note: "自動で0円（編集不可）" },
];

function statusBadge(status: MethodRow["status"]) {
  if (status === "ready") {
    return <span className="rounded bg-[#EAF3DE] px-2 py-0.5 text-[10px] font-semibold text-[#3B6D11]">反映済</span>;
  }
  return <span className="rounded border border-[#E0E0DC] bg-[#F5F5F3] px-2 py-0.5 text-[10px] font-semibold text-[#888]">未反映</span>;
}

function typeBadge(type: MethodRow["type"]) {
  if (type === "paid") {
    return <span className="rounded bg-[#EAF3DE] px-2 py-0.5 text-[10px] font-semibold text-[#3B6D11]">有料配送</span>;
  }
  return <span className="rounded border border-[#E0E0DC] bg-[#F5F5F3] px-2 py-0.5 text-[10px] font-semibold text-[#888]">発送なし</span>;
}

function pickCourierCost(rows: MethodRow[]) {
  const hit = rows.find((r) => r.name === "宅配便");
  return Number(hit?.cost ?? 0) || 0;
}

function pickMailCost(rows: MethodRow[]) {
  const hit = rows.find((r) => r.name === "追跡可能メール便") || rows.find((r) => r.name === "メール便");
  return Number(hit?.cost ?? 0) || 0;
}

function applyCosts(rows: MethodRow[], courierCost: number, mailCost: number): MethodRow[] {
  return rows.map((r): MethodRow => {
    if (r.type === "none") return r;
    if (r.name === "追跡可能メール便" || r.name === "メール便") {
      const status: MethodRow["status"] = mailCost > 0 ? "ready" : "missing";
      return { ...r, cost: mailCost, status };
    }
    if (r.name === "宅配便") {
      const status: MethodRow["status"] = courierCost > 0 ? "ready" : "missing";
      return { ...r, cost: courierCost, status };
    }
    return r;
  });
}

export default function ShippingCostSettingsPage() {
  const user = useUserContext();
  const locked = useMemo(() => isFreePlanUser(user) || isLightPlanUser(user), [user]);

  const [ym, setYm] = useState(() => dayjs().format("YYYY-MM"));
  const [avgCost, setAvgCost] = useState<number>(650);
  const [rows, setRows] = useState<MethodRow[]>(INITIAL_ROWS);
  const [mapping, setMapping] = useState<string>("");
  const [sourceMethod] = useState<string>("冷蔵便（ヤマト）");
  const [msgAvg, setMsgAvg] = useState(false);
  const [msgDetail, setMsgDetail] = useState(false);
  const [msgMatch, setMsgMatch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await apiGetJson(
        `/api/profit/shipping-cost-settings?yearMonth=${encodeURIComponent(ym)}`,
      )) as {
        averageShippingCost?: number;
        courierShippingCost?: number;
        mailShippingCost?: number;
      };

      const averageShippingCost = Number(res?.averageShippingCost ?? 0) || 0;
      const courierShippingCost = Number(res?.courierShippingCost ?? 0) || 0;
      const mailShippingCost = Number(res?.mailShippingCost ?? 0) || 0;

      setAvgCost(averageShippingCost);
      setRows((prev) => applyCosts(prev, courierShippingCost, mailShippingCost));

      const mappingRes = (await apiGetJson(
        `/api/profit/shipping-method-mappings?yearMonth=${encodeURIComponent(ym)}`,
      )) as { mappings?: Array<{ source_method?: string; target_method?: string }> };

      const hit = (mappingRes.mappings || []).find(
        (m) => String(m.source_method || "").trim() === sourceMethod,
      );
      const target = String(hit?.target_method || "").trim();

      if (target === "クール便") setMapping("cool");
      else if (target === "クール冷凍便") setMapping("freeze");
      else if (target === "宅配便") setMapping("normal");
      else setMapping("");
    } catch (e: any) {
      setError(e?.message || "配送コスト設定の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [ym]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await apiPutJson("/api/profit/shipping-cost-settings", {
        yearMonth: ym,
        averageShippingCost: Math.max(0, Math.trunc(avgCost || 0)),
        courierShippingCost: Math.max(0, Math.trunc(pickCourierCost(rows))),
        mailShippingCost: Math.max(0, Math.trunc(pickMailCost(rows))),
      });
      return true;
    } catch (e: any) {
      setError(e?.message || "配送コスト設定の保存に失敗しました。");
      return false;
    } finally {
      setSaving(false);
    }
  }, [avgCost, rows, ym]);

  return (
    <div className="mx-auto w-full max-w-[1200px] text-[13px] text-[#1a1a1a]">
      <div className="mb-1 text-[20px] font-semibold text-[#222]">配送コスト設定</div>
      <div className="mb-5 text-[12px] leading-6 text-[#888]">
        商品別利益の物流費計算に使用する配送コストを設定します。
      </div>

      <div className="mb-4 flex items-center gap-2">
        <span className="text-[12px] text-[#666]">対象月</span>
        <input
          type="month"
          className="rounded-md border border-[#ddd] px-2 py-1 text-[12px]"
          value={ym}
          onChange={(e) => setYm(e.target.value)}
          disabled={loading || saving}
        />
        {(loading || saving) && <span className="text-[11px] text-[#888]">{loading ? "読込中..." : "保存中..."}</span>}
      </div>

      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>}

      <div className="mb-4 rounded-lg border border-[#FAC775] bg-[#FEF3CD] px-4 py-3 text-[12px] text-[#854F0B]">
        受注データに <strong>「冷蔵便（ヤマト）」</strong> が存在しますが、設定にマッチする配送方法が見つかっていません。
      </div>

      <section className="mb-6 rounded-xl border border-[#E8E8E5] bg-white">
        <div className="border-b border-[#F0F0ED] px-5 py-4">
          <div className="flex items-center gap-2 text-[14px] font-semibold">
            平均配送コスト
            <span className="rounded border border-[#E0E0DC] bg-[#F5F5F3] px-2 py-0.5 text-[10px] text-[#777]">全プラン</span>
          </div>
          <div className="mt-1 text-[11px] text-[#999]">
            1配送あたりの平均コスト（税込・資材費込）を入力してください。
          </div>
        </div>

        <div className="px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[12px] text-[#555]">1配送あたりの平均コスト（税込・資材費込）</span>
            <input
              type="number"
              min={0}
              className="w-[140px] rounded-md border border-[#ddd] px-3 py-1.5 text-right font-semibold"
              value={avgCost}
              onChange={(e) => setAvgCost(Number(e.target.value || 0))}
            />
            <span className="text-[12px] text-[#666]">円</span>
          </div>
          <div className="mt-2 text-[11px] leading-5 text-[#999]">
            スタンダードプラン以上で配送方法別の詳細設定を行った場合、この平均値は使用されません。
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[#F0F0ED] bg-[#FAFAF8] px-5 py-3">
          {msgAvg && <span className="rounded border border-[#97C459] bg-[#EAF3DE] px-2 py-1 text-[11px] text-[#3B6D11]">保存しました</span>}
          <button className="rounded-md border border-[#ddd] bg-white px-4 py-1.5 text-[12px] text-[#666]">キャンセル</button>
          <button
            className="rounded-md border border-[#85B7EB] bg-[#EBF3FB] px-4 py-1.5 text-[12px] font-semibold text-[#185FA5]"
            disabled={saving}
            onClick={async () => {
              const ok = await saveSettings();
              if (!ok) return;
              setMsgAvg(true);
              window.setTimeout(() => setMsgAvg(false), 2500);
            }}
          >
            保存する
          </button>
        </div>
      </section>

      <section className="relative mb-6">
        <div className="mb-2 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-[14px] font-semibold text-[#333]">
              配送方法別コスト設定
              <span className="rounded border border-[#B5D4F4] bg-[#EBF3FB] px-2 py-0.5 text-[10px] text-[#185FA5]">スタンダード以上</span>
            </div>
            <div className="mt-1 text-[11px] text-[#999]">
              RMSに登録されている配送方法ごとに、より正確なコストを設定できます。
            </div>
          </div>
        </div>

        <div className={`${locked ? "pointer-events-none select-none blur-[1px] grayscale-[20%]" : ""}`}>
          <div className="overflow-hidden rounded-xl border border-[#E8E8E5] bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-[980px] w-full border-collapse text-[12px]">
                <thead>
                  <tr className="bg-[#F0F0ED] text-[#888]">
                    <th className="px-3 py-2 text-left">配送方法</th>
                    <th className="px-3 py-2 text-left">反映状態</th>
                    <th className="px-3 py-2 text-left">1配送あたり平均コスト</th>
                    <th className="px-3 py-2 text-left">種別</th>
                    <th className="px-3 py-2 text-left">備考</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const disabled = row.type === "none";
                    return (
                      <tr key={row.name} className="border-t border-[#EBEBEA] even:bg-[#F8F8F6]">
                        <td className="px-3 py-2">
                          <div className="font-medium text-[#111]">{row.name}</div>
                          <div className="text-[10px] text-[#bbb]">{row.carrier}</div>
                        </td>
                        <td className="px-3 py-2">{statusBadge(row.status)}</td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            disabled={disabled}
                            className="w-[110px] rounded-md border border-[#ddd] px-2 py-1 text-right disabled:bg-[#F5F5F3] disabled:text-[#bbb]"
                            value={row.cost ?? ""}
                            onChange={(e) => {
                              const next = e.target.value === "" ? null : Number(e.target.value);
                              setRows((prev) =>
                                prev.map((x) => (x.name === row.name ? { ...x, cost: next } : x)),
                              );
                            }}
                          />
                          <span className="ml-1 text-[11px] text-[#999]">円</span>
                        </td>
                        <td className="px-3 py-2">{typeBadge(row.type)}</td>
                        <td className="px-3 py-2 text-[11px] text-[#999]">{row.note}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-[#F0F0ED] bg-[#FAFAF8] px-5 py-3">
              {msgDetail && <span className="rounded border border-[#97C459] bg-[#EAF3DE] px-2 py-1 text-[11px] text-[#3B6D11]">保存しました</span>}
              <button className="rounded-md border border-[#ddd] bg-white px-4 py-1.5 text-[12px] text-[#666]">キャンセル</button>
              <button
                className="rounded-md border border-[#85B7EB] bg-[#EBF3FB] px-4 py-1.5 text-[12px] font-semibold text-[#185FA5]"
                disabled={saving}
                onClick={async () => {
                  const ok = await saveSettings();
                  if (!ok) return;
                  setMsgDetail(true);
                  window.setTimeout(() => setMsgDetail(false), 2500);
                }}
              >
                設定を保存する
              </button>
            </div>
          </div>

          <div className="mt-3 overflow-hidden rounded-xl border border-[#E8E8E5] bg-white">
            <div className="border-b border-[#F0F0ED] px-5 py-4">
              <div className="text-[13px] font-semibold text-[#333]">マッチング未対応の配送方法</div>
              <div className="mt-1 text-[11px] text-[#999]">受注データに存在するが設定に一致しない配送方法です。</div>
            </div>
            <div className="px-5 py-4">
              <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
                <div>
                  <div className="text-[12px] font-medium text-[#111]">冷蔵便（ヤマト）</div>
                  <div className="text-[11px] text-[#999]">出現件数 23件</div>
                </div>
                <select
                  className="min-w-[220px] rounded-md border border-[#ddd] px-2 py-1.5 text-[12px]"
                  value={mapping}
                  onChange={(e) => setMapping(e.target.value)}
                >
                  <option value="">- 選択してください -</option>
                  <option value="cool">クール便（920円）</option>
                  <option value="freeze">クール冷凍便（1080円）</option>
                  <option value="normal">宅配便（720円）</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[#F0F0ED] bg-[#FAFAF8] px-5 py-3">
              {msgMatch && <span className="rounded border border-[#97C459] bg-[#EAF3DE] px-2 py-1 text-[11px] text-[#3B6D11]">保存しました</span>}
              <button className="rounded-md border border-[#ddd] bg-white px-4 py-1.5 text-[12px] text-[#666]">キャンセル</button>
              <button
                className="rounded-md border border-[#85B7EB] bg-[#EBF3FB] px-4 py-1.5 text-[12px] font-semibold text-[#185FA5]"
                disabled={saving}
                onClick={async () => {
                  const toTarget =
                    mapping === "cool"
                      ? "クール便"
                      : mapping === "freeze"
                        ? "クール冷凍便"
                        : mapping === "normal"
                          ? "宅配便"
                          : "";

                  try {
                    setSaving(true);
                    setError(null);
                    await apiPutJson("/api/profit/shipping-method-mappings", {
                      yearMonth: ym,
                      sourceMethod,
                      targetMethod: toTarget,
                    });
                    setMsgMatch(true);
                    window.setTimeout(() => setMsgMatch(false), 2500);
                  } catch (e: any) {
                    setError(e?.message || "マッチング保存に失敗しました。");
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                マッチングを保存する
              </button>
            </div>
          </div>
        </div>

        {locked && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-[#F5F5F3]/80 p-4">
            <div className="max-w-[420px] rounded-xl border border-[#E8E8E5] bg-white px-6 py-5 text-center shadow-[0_6px_24px_rgba(0,0,0,0.1)]">
              <div className="mb-2 text-[24px]">🔒</div>
              <div className="mb-2 text-[15px] font-semibold text-[#111]">スタンダードプラン以上で利用できます</div>
              <div className="mb-4 text-[12px] leading-6 text-[#888]">
                配送方法別の詳細設定を行うことで、商品別の物流費をより正確に計算できます。
              </div>
              <button
                className="rounded-md bg-[#185FA5] px-5 py-2 text-[12px] font-semibold text-white hover:bg-[#0E4A82]"
                onClick={() => openUpgradeForm("送料詳細設定")}
              >
                プランをアップグレード
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
